import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import disableSubtitleTracks from 'components/Dashboard/ParliamentLive/disableSubtitleTracks';

const THUMBNAIL_COUNT = 10;
const THUMBNAIL_WIDTH = 128;
const THUMBNAIL_HEIGHT = 72;
const POSTER_WIDTH = 640;
const POSTER_HEIGHT = 360;

// A seek past the real end of the video never fires 'seeked'; resolve anyway after this so the
// sequence can't hang. Overall thumbnail capture is also capped, so slow seeks can't add up.
const SEEK_TIMEOUT_MS = 4000;
const THUMBNAIL_BUDGET_MS = 10000;

const seekTo = (player, seconds) =>
  new Promise((resolve) => {
    if (Math.abs(player.currentTime() - seconds) < 0.15) {
      resolve();
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timeoutId = setTimeout(finish, SEEK_TIMEOUT_MS);
    player.one('seeked', () => {
      clearTimeout(timeoutId);
      requestAnimationFrame(finish);
    });
    player.currentTime(seconds);
  });

const drawFrame = (player, width, height, quality) => {
  const videoEl = player.tech({ IWillNotUseThisInPlugins: true }).el();
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(videoEl, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
};

const capturePoster = (player) => {
  try {
    return drawFrame(player, POSTER_WIDTH, POSTER_HEIGHT, 0.8);
  } catch (error) {
    return null;
  }
};

// Forward, monotonic seeking lets VHS prefetch ahead, pre-warming the buffer for later drag seeks.
// onFrame gets each thumbnail as it's captured so callers can render the filmstrip progressively.
// Sampled at cell centres ((i + 0.5) / N) so each frame lines up with its equal-width filmstrip
// slot — and therefore with the selection overlay, which is positioned by true time fraction.
const captureThumbnails = async (player, windowStart, windowEnd, onFrame) => {
  const step = (windowEnd - windowStart) / THUMBNAIL_COUNT;
  const frames = [];
  const deadline = Date.now() + THUMBNAIL_BUDGET_MS;
  for (let i = 0; i < THUMBNAIL_COUNT && Date.now() < deadline; i++) {
    await seekTo(player, windowStart + step * (i + 0.5));
    try {
      const frame = drawFrame(player, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT, 0.7);
      frames.push(frame);
      onFrame?.(frame);
    } catch (error) {
      // tainted/unavailable frame — skip
    }
  }
  return frames;
};

// Streams the source HLS directly (no ffmpeg) for a fast preview; the logo is overlaid live.
// windowStart/windowEnd drive mount/remount; clipStart/clipEnd are read from refs so dragging
// the trim never remounts the player. The offscreen preparer passes onReady/onPoster/onFrame;
// the modal passes a ready `poster` to show instantly while its own player warms up.
const ClipPreviewPlayer = forwardRef(
  (
    {
      masterPlaylistUrl,
      windowStart,
      windowEnd,
      clipStart,
      clipEnd,
      logoUrl,
      poster,
      onThumbnails,
      onFrame,
      onReady,
      onPoster,
      onPlayingChange,
      onTimeUpdate,
      skipThumbnailCapture,
    },
    ref
  ) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [ready, setReady] = useState(false);
    const clipStartRef = useRef(clipStart);
    const clipEndRef = useRef(clipEnd);
    clipStartRef.current = clipStart;
    clipEndRef.current = clipEnd;

    useEffect(() => {
      setReady(false);
      const videoElement = document.createElement('video-js');
      containerRef.current.appendChild(videoElement);

      const player = videojs(videoElement, {
        controls: false,
        fluid: true,
        preload: 'auto',
        crossOrigin: 'anonymous',
        sources: [{ src: masterPlaylistUrl, type: 'application/x-mpegURL' }],
      });
      playerRef.current = player;
      disableSubtitleTracks(player);

      player.one('loadeddata', async () => {
        // Bounds were computed upstream from a possibly-stale videoDuration — clamp to what this
        // player now reports so we never seek past the real end.
        const playerDuration = player.duration();
        const hasKnownDuration = Number.isFinite(playerDuration) && playerDuration > 0;
        const safeWindowEnd = hasKnownDuration ? Math.min(windowEnd, playerDuration) : windowEnd;
        const safeClipStart = hasKnownDuration
          ? Math.min(clipStartRef.current, playerDuration)
          : clipStartRef.current;

        await seekTo(player, safeClipStart);
        onPoster?.(capturePoster(player));
        onReady?.();
        setReady(true);

        if (!skipThumbnailCapture) {
          try {
            onThumbnails?.(await captureThumbnails(player, windowStart, safeWindowEnd, onFrame));
          } catch (error) {
            onThumbnails?.([]);
          }
        }
      });
      player.on('play', () => {
        onPlayingChange?.(true);
      });
      player.on('pause', () => {
        onPlayingChange?.(false);
      });
      player.on('timeupdate', () => {
        onTimeUpdate?.(player.currentTime());
        if (player.currentTime() >= clipEndRef.current) {
          player.pause();
          player.currentTime(clipStartRef.current);
        }
      });

      return () => {
        player.dispose();
        playerRef.current = null;
      };
    }, [masterPlaylistUrl, windowStart, windowEnd]);

    useImperativeHandle(ref, () => ({
      // "Play" only ever means "preview the selected clip" — no scrubber to resume from.
      togglePlay: () => {
        const player = playerRef.current;
        if (!player) return;
        if (player.paused()) {
          player.currentTime(clipStartRef.current);
          player.play();
        } else {
          player.pause();
        }
      },
      showFrame: (seconds) => {
        const player = playerRef.current;
        if (!player) return;
        player.pause();
        player.currentTime(seconds);
      },
    }));

    return (
      <div className='position-relative w-100 h-100'>
        <div data-vjs-player className='w-100 h-100 parliament-live-video-player'>
          <div ref={containerRef} className='w-100 h-100' />
        </div>
        <img
          src={logoUrl}
          alt=''
          className='position-absolute'
          style={{ top: 16, left: 16, height: '25px', pointerEvents: 'none' }}
        />
        {!ready &&
          (poster ? (
            <img
              src={poster}
              alt=''
              className='position-absolute w-100 h-100'
              style={{ top: 0, left: 0, objectFit: 'cover' }}
            />
          ) : (
            <div
              className='bg-main-white position-absolute w-100 h-100 flex-centered justify-content-center flex-column text-center px-3'
              style={{ top: 0, left: 0 }}
            >
              <img src={`${import.meta.env.VITE_APP_CDNURL}/images/loader.gif`} alt='Loading' />
              <p className='paragraph-p4 mt-3 mb-0'>Preparing preview…</p>
            </div>
          ))}
      </div>
    );
  }
);

export default ClipPreviewPlayer;
